import { useQuery } from "@apollo/client/react";
import { useContext } from "react";
import { useIntl } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { SearchPersonsDocument } from "@/graphql/generated/operations";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { personNameKey, type PersonMention } from "@/lib/person-mentions";
import { useGlobalPersonSearch } from "@/lib/use-global-person-search";
import { useAgentStore } from "@/stores/agent-store";
import { PersonChoiceContext, type PersonChoice } from "./person-choice";

/**
 * Where a reply asks which person the user means, each candidate as the person card of the extended search, in
 * its grid, the whole card the choice (Marvin, 2026-09-29). A candidate named without an id is looked up by
 * name, and every person of that name is shown, so two people of the same name become two cards to tell apart;
 * a name nobody in the system carries shows nothing.
 */
export function PersonChoiceCards({ mentions }: { mentions: readonly PersonMention[] }) {
  const intl = useIntl();
  const choose = useContext(PersonChoiceContext);
  const candidates = mentions.filter((mention) => mention.choose);
  if (!choose || !candidates.length) return null;
  return (
    <ul aria-label={intl.formatMessage({ id: "agent.personChoice.label", defaultMessage: "People to choose from" })} className="mb-2 mt-3 grid grid-cols-1 gap-3 p-0.5 @sm:grid-cols-2">
      {candidates.map((mention) => (
        <Candidate key={mention.id ?? personNameKey(mention.name)} mention={mention} onChoose={choose} />
      ))}
    </ul>
  );
}

// Every word of the name the reply wrote is in the person's name: the search is fuzzy, a choice is not.
const namesMatch = (asked: string, name: string) => {
  const own = personNameKey(name).split(" ");
  return personNameKey(asked).split(" ").every((word) => own.includes(word));
};

function Candidate({ mention, onChoose }: { mention: PersonMention; onChoose: (choice: PersonChoice) => void }) {
  const byName = useGlobalPersonSearch({ query: mention.name, personFilter: null, pageSize: 5, skip: Boolean(mention.id) });
  const byId = useQuery(SearchPersonsDocument, {
    variables: { input: { where: { id: { in: mention.id ? [mention.id] : [] } }, first: 1 } },
    skip: !mention.id,
  });
  const chosenId = useAgentStore((s) => s.personChoices[personNameKey(mention.name)]);
  const people = mention.id
    ? byId.data?.persons.nodes ?? []
    : byName.nodes.filter((person) => namesMatch(mention.name, personDisplayName(person)));
  return (
    <>
      {people.map((person) => {
        const name = personDisplayName(person);
        const role = personDisplayRole(person);
        // Role and city in the answer sent back tell two people of the same name apart; the card shows the role.
        const detail = [role, person.city].filter(Boolean).join(" · ") || null;
        return (
          <li key={person.id}>
            <PersonResultRow
              id={`agent-person-choice-${person.id}`}
              person={{ id: person.id, name, avatarContent: person.avatar?.content ?? null, avatarMime: person.avatar?.mimeType ?? null }}
              title={name}
              sub={role}
              selected={chosenId === person.id}
              onSelect={() => chosenId !== person.id && onChoose({ id: person.id, name, asked: mention.name, detail })}
            />
          </li>
        );
      })}
    </>
  );
}
